import { useLayoutEffect, useRef, useState } from "react"
import {
	cancelFrame,
	frame,
	motion,
	motionValue,
	useMotionValue,
	useMotionValueEvent,
	useTransform,
} from "framer-motion"

// ──────────────────────────────────────────────────────────────────────────
// DAL GIORNO ALLA SETTIMANA, UN PO' ALLA VOLTA
//
// Sul telefono giorno e settimana sono la stessa pagina a due misure: un
// avanzamento da 0 (il giorno) a 1 (la settimana) che può fermarsi a metà,
// perché lo muovono le dita (vedi usePinchToSwitchView). Tutto quello che
// cambia nel passaggio — le schede che si stringono in righe, gli altri
// giorni che si aprono, i titoli — si legge da lì.
//
// Fermi a un capo si monta solo quello che si vede; in mezzo ci sono le due
// forme, una sopra l'altra, e si passa dall'una all'altra.
// ──────────────────────────────────────────────────────────────────────────

// Quanto dura il passaggio quando non lo guidano le dita, e con che andatura
export const VIEW_SWITCH_TRANSITION = {
	ease: [0.4, 0, 0.2, 1],
	duration: 0.45,
}

// L'avanzamento di chi è sempre nella forma della settimana: le righe degli
// altri giorni, che non diventano mai schede.
export const WEEK_PROGRESS = motionValue(1)

export const lerp = (from, to, t) => from + (to - from) * t

const modeOf = progress => (progress <= 0 ? "day" : progress >= 1 ? "week" : "moving")

// "day" e "week" da fermi, "moving" nel mezzo. Cambia solo passando da uno
// all'altro: le forme intermedie non ridisegnano React, solo gli stili.
export const useMorphMode = progress => {
	const [mode, setMode] = useState(() => modeOf(progress.get()))
	useMotionValueEvent(progress, "change", value => setMode(modeOf(value)))
	return mode
}

// L'altezza di un elemento, misurata appena montato — prima di disegnarlo —
// e poi ogni volta che cambia (un'immagine che arriva, un testo che va a
// capo): il posto che lo contiene la segue.
const useHeight = (ref, height) => {
	useLayoutEffect(() => {
		const element = ref.current
		if (!element) return
		height.set(element.offsetHeight)
		if (typeof ResizeObserver === "undefined") return
		const observer = new ResizeObserver(() => height.set(element.offsetHeight))
		observer.observe(element)
		return () => observer.disconnect()
	}, [ref, height])
}

// Due forme dello stesso posto: `day` da fermi sul giorno, `week` sulla
// settimana. Nel mezzo stanno una sopra l'altra, la prima che svanisce e la
// seconda che compare, e il posto cresce o cala dall'altezza dell'una a
// quella dell'altra. Una delle due può mancare: allora il posto nasce o si
// chiude.
//
// `flying` sono i pezzi che le due forme hanno in comune (segnati in tutte e
// due con `data-morph`): invece di svanire in una e comparire nell'altra,
// volano dal loro posto nell'una al loro posto nell'altra (vedi useFlights).
export function MorphLayers({ progress, mode, day = null, week = null, flying }) {
	if (mode === "day") return day
	if (mode === "week") return week
	return <MovingLayers progress={progress} day={day} week={week} flying={flying} />
}

function MovingLayers({ progress, day, week, flying }) {
	const containerRef = useRef(null)
	const dayRef = useRef(null)
	const weekRef = useRef(null)
	useFlights(progress, containerRef, dayRef, weekRef, flying)
	const dayHeight = useMotionValue(0)
	const weekHeight = useMotionValue(0)
	// prima l'altezza che ascolta le misure, poi le misure: al contrario la
	// prima misura arriverebbe prima che qualcuno ascolti, e il posto
	// resterebbe chiuso finché le dita non si muovono
	const height = useTransform([progress, dayHeight, weekHeight], ([t, from, to]) =>
		lerp(from, to, t),
	)
	useHeight(dayRef, dayHeight)
	useHeight(weekRef, weekHeight)
	// chi se ne va svanisce mentre chi arriva compare: si incrociano a metà,
	// così il posto non resta mai vuoto. Con dei pezzi in volo il posto non
	// è mai vuoto comunque, e il resto della forma d'arrivo aspetta che i
	// pezzi siano quasi arrivati: comparendo prima ci passerebbero sopra
	const dayOpacity = useTransform(progress, [0, flying ? 0.45 : 0.55], [1, 0])
	const weekOpacity = useTransform(progress, [flying ? 0.65 : 0.45, 1], [0, 1])

	const layer = { position: "absolute", top: 0, left: 0, right: 0 }
	return (
		<motion.div ref={containerRef} style={{ position: "relative", height, overflow: "hidden" }}>
			{day && (
				<motion.div ref={dayRef} style={{ ...layer, opacity: dayOpacity }}>
					{day}
				</motion.div>
			)}
			{week && (
				<motion.div ref={weekRef} style={{ ...layer, opacity: weekOpacity }} aria-hidden>
					{week}
				</motion.div>
			)}
		</motion.div>
	)
}

// ── i pezzi in volo ───────────────────────────────────────────────────────
// Lo stesso pezzo — l'orario, i nomi, il campo — sta in un posto della
// scheda e in un altro della riga, grande in una e piccolo nell'altra, bold
// in una e regular nell'altra. Mentre si passa dall'una all'altra i due
// originali si nascondono, e due loro copie volano insieme dal primo posto
// al secondo, crescendo o calando come il testo: la copia della forma da cui
// si parte svanisce, quella della forma dove si arriva compare. Così il
// regular diventa bold mentre si sposta, invece di saltare.
//
// Le copie sono copie della pagina, non di React: vivono quanto il
// passaggio, e le loro posizioni si scrivono a ogni fotogramma senza
// ridisegnare niente.

const clamp01 = value => Math.min(1, Math.max(0, value))

// Quanto è grande il testo di un pezzo: quello del primo elemento che ha
// delle lettere sue, il riferimento per farlo crescere o calare
const textSizeOf = element => {
	const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT)
	for (let node = walker.nextNode(); node; node = walker.nextNode())
		if (node.textContent.trim()) return parseFloat(getComputedStyle(node.parentElement).fontSize)
	return parseFloat(getComputedStyle(element).fontSize)
}

// La copia che vola: larga quanto l'originale, così va a capo come lui
const flyingCopy = element => {
	const copy = element.cloneNode(true)
	copy.removeAttribute("data-morph")
	copy.setAttribute("aria-hidden", "true")
	Object.assign(copy.style, {
		position: "absolute",
		left: "0",
		top: "0",
		margin: "0",
		width: `${element.getBoundingClientRect().width}px`,
		transformOrigin: "0 0",
		visibility: "visible",
		pointerEvents: "none",
		willChange: "transform, opacity",
	})
	return copy
}

function useFlights(progress, containerRef, dayRef, weekRef, keys) {
	useLayoutEffect(() => {
		const container = containerRef.current
		if (!keys?.length || !container || !dayRef.current || !weekRef.current) return

		const overlay = document.createElement("div")
		Object.assign(overlay.style, { position: "absolute", inset: "0", pointerEvents: "none" })
		container.appendChild(overlay)

		const flights = keys
			.map(key => {
				const from = dayRef.current.querySelector(`[data-morph="${key}"]`)
				const to = weekRef.current.querySelector(`[data-morph="${key}"]`)
				// un pezzo che c'è in una forma sola svanisce con lei
				if (!from || !to) return null
				const flight = {
					from,
					to,
					fromCopy: flyingCopy(from),
					toCopy: flyingCopy(to),
					fromSize: textSizeOf(from),
					toSize: textSizeOf(to),
				}
				overlay.append(flight.fromCopy, flight.toCopy)
				from.style.visibility = "hidden"
				to.style.visibility = "hidden"
				return flight
			})
			.filter(Boolean)

		// dove stanno gli originali, rispetto al posto che li contiene
		const boxOf = element => {
			const box = element.getBoundingClientRect()
			const origin = container.getBoundingClientRect()
			return { x: box.left - origin.left, y: box.top - origin.top }
		}
		let boxes = []
		const measure = () => {
			boxes = flights.map(({ from, to }) => ({ from: boxOf(from), to: boxOf(to) }))
		}
		const paint = () => {
			const t = progress.get()
			// di lato presto, in su o in giù tardi: i pezzi fanno una curva, e i
			// nomi arrivano accanto all'orario quando si è già stretto, invece di
			// finirgli sopra
			const across = 1 - (1 - t) * (1 - t)
			const along = Math.pow(t, 1.5)
			flights.forEach(({ fromCopy, toCopy, fromSize, toSize }, i) => {
				const { from, to } = boxes[i]
				const x = lerp(from.x, to.x, across)
				const y = lerp(from.y, to.y, along)
				const size = lerp(fromSize, toSize, t)
				fromCopy.style.transform = `translate(${x}px, ${y}px) scale(${size / fromSize})`
				toCopy.style.transform = `translate(${x}px, ${y}px) scale(${size / toSize})`
				fromCopy.style.opacity = clamp01(1 - t / 0.6)
				toCopy.style.opacity = clamp01((t - 0.4) / 0.6)
			})
		}
		measure()
		paint()
		// le misure prima che il fotogramma cambi qualcosa, le posizioni dopo
		const stop = progress.on("change", () => {
			frame.read(measure)
			frame.render(paint)
		})
		return () => {
			stop()
			cancelFrame(measure)
			cancelFrame(paint)
			overlay.remove()
			flights.forEach(({ from, to }) => {
				from.style.visibility = ""
				to.style.visibility = ""
			})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])
}

// Un valore che va da `day` a `week` con l'avanzamento: margini, spigoli,
// spazi interni.
export const useLerp = (progress, day, week) => useTransform(progress, t => lerp(day, week, t))
